import { useEffect, useRef } from 'react'
import { openAiAssistant } from '../../aiAssistant/aiAssistantBridge'

/**
 * Right Option/Alt on its own is the dictation key: hold to talk, tap to toggle
 * hands-free. Left Alt stays free for the app's Alt shortcuts.
 */
const DICTATION_KEY = 'AltRight'
// Long enough that right Alt used as a modifier (Alt+letter) never starts dictation.
const HOLD_DELAY_MS = 200

export type DictationTarget = {
    start: () => void
    stop: () => void
    cancel: () => void
    toggle: () => void
}

let target: DictationTarget | null = null
// Set when the key is pressed while the composer is not mounted; it starts on mount.
let pendingStart = false

function beginDictation(action: 'start' | 'toggle') {
    if (target) {
        target[action]()
        return
    }
    pendingStart = true
    openAiAssistant()
}

/** Composer side: receives hotkey gestures while it is mounted. */
export function useDictationTarget(handlers: DictationTarget) {
    const handlersRef = useRef(handlers)
    handlersRef.current = handlers

    useEffect(() => {
        const proxy: DictationTarget = {
            start: () => handlersRef.current.start(),
            stop: () => handlersRef.current.stop(),
            cancel: () => handlersRef.current.cancel(),
            toggle: () => handlersRef.current.toggle(),
        }
        target = proxy
        if (pendingStart) {
            pendingStart = false
            proxy.start()
        }
        return () => {
            if (target === proxy) target = null
        }
    }, [])
}

/** App side: listens for the hotkey everywhere, opening the assistant if needed. */
export function useDictationHotkey(enabled: boolean) {
    useEffect(() => {
        if (!enabled) return
        let holdTimer: number | undefined
        let keyDown = false
        let engaged = false

        const reset = () => {
            window.clearTimeout(holdTimer)
            holdTimer = undefined
            keyDown = false
            engaged = false
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.code === DICTATION_KEY) {
                // Ctrl is set for AltGr on non-US layouts, where right Alt types characters.
                if (e.repeat || keyDown || e.ctrlKey || e.metaKey || e.shiftKey) return
                keyDown = true
                holdTimer = window.setTimeout(() => {
                    holdTimer = undefined
                    engaged = true
                    beginDictation('start')
                }, HOLD_DELAY_MS)
                return
            }
            if (!keyDown) return
            // Another key while held: right Alt is being used as a modifier.
            if (engaged) target?.cancel()
            pendingStart = false
            reset()
        }

        const onKeyUp = (e: KeyboardEvent) => {
            if (e.code !== DICTATION_KEY || !keyDown) return
            // Keeps Firefox on Windows from moving focus to its menu bar.
            e.preventDefault()
            const tapped = holdTimer !== undefined
            const wasEngaged = engaged
            reset()
            if (tapped) {
                beginDictation('toggle')
            } else if (wasEngaged) {
                pendingStart = false
                target?.stop()
            }
        }

        // The keyup is lost if the window loses focus mid-hold.
        const onBlur = () => {
            if (engaged) {
                pendingStart = false
                target?.stop()
            }
            reset()
        }

        window.addEventListener('keydown', onKeyDown, true)
        window.addEventListener('keyup', onKeyUp, true)
        window.addEventListener('blur', onBlur)
        return () => {
            reset()
            window.removeEventListener('keydown', onKeyDown, true)
            window.removeEventListener('keyup', onKeyUp, true)
            window.removeEventListener('blur', onBlur)
        }
    }, [enabled])
}
